import { useEffect, useMemo, useRef, useState } from 'react'

import { checkDate, checkDateTime, todayThai } from './datetime'

/* ============================================================================
   ปฏิทินเลือกวันที่ — เขียนเอง ไม่ใช้ <input type="date">

   ⚠️ ทำไมไม่ใช้ <input type="date">
      ปฏิทินของ native input มาจากเบราว์เซอร์+ระบบปฏิบัติการของผู้ใช้ เครื่องที่ตั้ง
      locale ไทยจะเห็นปฏิทิน **พ.ศ.** และรูปแบบที่โชว์ต่างกันไปในแต่ละเครื่อง
      (dd/mm/yyyy, mm/dd/yyyy, พ.ศ., ค.ศ.) — คนกรอกจึงอ่านคนละอย่างกันทั้งที่
      ค่าที่ส่งขึ้น backend เป็น YYYY-MM-DD เหมือนกัน กลายเป็นเลือกผิดปีโดยไม่รู้ตัว
      ตัวนี้วาดปฏิทินเองทั้งหมด **ค.ศ. เสมอ ทุกเครื่องเห็นเหมือนกัน**

   ⚠️ คิดวันที่ด้วย Date.UTC เท่านั้น ห้าม new Date(y, m, d) (ตัวหลังคิดตาม timezone
      ของเครื่อง เครื่องที่โซนติดลบจะได้วันเลื่อนไป 1 วัน)

   สไตล์อยู่ที่ .dp* ใน src/index.css
   ============================================================================ */

export const DOW_TH = ['อา.', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.']
/** เรียงแบบขึ้นต้นวันจันทร์ — คู่กับ monthGrid(y, m, true) */
export const DOW_TH_MON = ['จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.', 'อา.']
export const MONTH_TH = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม',
]

const p2 = (n: number) => String(n).padStart(2, '0')
const ymd = (y: number, m: number, d: number) => `${y}-${p2(m)}-${p2(d)}`

/** จำนวนวันในเดือน — วันที่ 0 ของเดือนถัดไป = วันสุดท้ายของเดือนนี้ */
const daysInMonth = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate()
/** วันในสัปดาห์ของวันที่ 1 (0 = อาทิตย์) */
const firstDow = (y: number, m: number) => new Date(Date.UTC(y, m - 1, 1)).getUTCDay()

export type Cell = { day: number; iso: string } | null

/** ตารางวัน 6 แถว × 7 ช่อง — ช่องที่ไม่ใช่ของเดือนนี้เป็น null (เว้นว่าง ไม่โชว์วันเดือนอื่น
 *  เพราะโชว์แล้วคนกดผิดเดือนบ่อย ทั้งที่ตัวเลขดูถูก)
 *
 *  @param mondayFirst สัปดาห์เริ่มวันจันทร์ — ใช้กับปฏิทินตารางกะ ที่การวนกะ
 *  นับสัปดาห์จากวันจันทร์อยู่แล้ว ถ้าปฏิทินขึ้นต้นวันอาทิตย์ แถบสีของแต่ละ
 *  สัปดาห์จะเหลื่อมกับแถวในตาราง มองไม่ออกว่าสลับกะตรงไหน
 *  (ตัวเลือกวันที่ทั่วไปยังขึ้นต้นวันอาทิตย์เหมือนเดิม — ปฏิทินไทยส่วนใหญ่เป็นแบบนั้น) */
export function monthGrid(year: number, month: number, mondayFirst = false): Cell[][] {
  const total = daysInMonth(year, month)
  const dow = firstDow(year, month)
  const lead = mondayFirst ? (dow + 6) % 7 : dow
  const cells: Cell[] = Array.from({ length: lead }, () => null)
  for (let d = 1; d <= total; d++) cells.push({ day: d, iso: ymd(year, month, d) })
  while (cells.length % 7 !== 0) cells.push(null)
  const rows: Cell[][] = []
  for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7))
  return rows
}

function Icon({ d }: { d: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  )
}

const CAL_ICON = 'M7 3v3M17 3v3M4 8h16M5 5h14a1 1 0 011 1v13a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1z'

/* ───────────────────────────── ปฏิทิน (แผงเดียว) ───────────────────────────── */

function Calendar({
  value,
  min,
  max,
  onPick,
}: {
  value: string
  min?: string
  max?: string
  onPick: (iso: string) => void
}) {
  const today = todayThai()
  const base = checkDate(value).ok ? value : today
  const [view, setView] = useState({ y: Number(base.slice(0, 4)), m: Number(base.slice(5, 7)) })

  const rows = useMemo(() => monthGrid(view.y, view.m), [view])

  const shiftMonth = (by: number) => {
    // คิดผ่าน Date.UTC ให้ข้ามปีเองถูก ไม่ต้องเขียนเงื่อนไข m<1 / m>12
    const at = new Date(Date.UTC(view.y, view.m - 1 + by, 1))
    setView({ y: at.getUTCFullYear(), m: at.getUTCMonth() + 1 })
  }

  // Boolean() ครอบไว้ — min/max เป็น string ที่อาจเป็น '' ซึ่งทำให้ && คืน '' ไม่ใช่ false
  const blocked = (iso: string) => Boolean((min && iso < min) || (max && iso > max))

  /** ปีที่ให้เลือกใน dropdown — กว้างพอใช้งานจริง (ย้อน 5 ปี ล่วงหน้า 2 ปี)
   *  ถ้ามี min/max ก็ขยายให้ครอบค่าที่ตั้งไว้ ไม่งั้นเลือกไปถึงไม่ได้ */
  const years = useMemo(() => {
    const now = Number(today.slice(0, 4))
    let lo = now - 5
    let hi = now + 2
    if (min) lo = Math.min(lo, Number(min.slice(0, 4)))
    if (max) hi = Math.max(hi, Number(max.slice(0, 4)))
    lo = Math.min(lo, view.y)
    hi = Math.max(hi, view.y)
    return Array.from({ length: hi - lo + 1 }, (_, i) => lo + i)
  }, [today, min, max, view.y])

  return (
    <div className="dp__cal">
      <div className="dp__head">
        <button type="button" className="dp__nav" onClick={() => shiftMonth(-1)} aria-label="เดือนก่อนหน้า">
          <Icon d="M15 19l-7-7 7-7" />
        </button>

        {/* เดือน/ปี เป็น dropdown ไม่ใช่แค่ลูกศร — ย้อนไปหลายเดือนจะได้ไม่ต้องกดรัว
            ⚠️ ปีเป็นเลข ค.ศ. ตรง ๆ ไม่มีการแปลงใด ๆ */}
        <div className="dp__pick">
          <select
            value={view.m}
            onChange={(e) => setView((v) => ({ ...v, m: Number(e.target.value) }))}
            aria-label="เดือน"
          >
            {MONTH_TH.map((name, i) => (
              <option key={name} value={i + 1}>
                {name}
              </option>
            ))}
          </select>
          <select
            value={view.y}
            onChange={(e) => setView((v) => ({ ...v, y: Number(e.target.value) }))}
            aria-label="ปี (ค.ศ.)"
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>

        <button type="button" className="dp__nav" onClick={() => shiftMonth(1)} aria-label="เดือนถัดไป">
          <Icon d="M9 5l7 7-7 7" />
        </button>
      </div>

      <div className="dp__dow" aria-hidden="true">
        {DOW_TH.map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>

      <div className="dp__grid" role="grid">
        {rows.map((row, ri) =>
          row.map((cell, ci) =>
            cell === null ? (
              <span key={`${ri}-${ci}`} className="dp__empty" />
            ) : (
              <button
                key={cell.iso}
                type="button"
                className="dp__day"
                onClick={() => onPick(cell.iso)}
                disabled={blocked(cell.iso)}
                aria-current={cell.iso === value ? 'date' : undefined}
                data-today={cell.iso === today ? '' : undefined}
                aria-label={cell.iso}
              >
                {cell.day}
              </button>
            ),
          ),
        )}
      </div>

      <div className="dp__foot">
        <button type="button" className="ghost tiny" onClick={() => onPick(today)}>
          วันนี้ ({today})
        </button>
      </div>
    </div>
  )
}

/* ───────────────────── ช่องกรอก + ปฏิทินที่กดเปิด ───────────────────── */

function useCloseOnOutside(open: boolean, close: () => void) {
  const box = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) close()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, close])
  return box
}

export function DatePicker({
  value,
  onChange,
  min,
  max,
  id,
  allowEmpty = true,
}: {
  /** `YYYY-MM-DD` หรือ '' */
  value: string
  onChange: (value: string) => void
  min?: string
  max?: string
  id?: string
  /** ล้างค่าได้ไหม (ตัวกรองล้างได้ · ช่องบังคับกรอกไม่ได้) */
  allowEmpty?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState(value)
  const [error, setError] = useState<string | null>(null)
  const box = useCloseOnOutside(open, () => setOpen(false))

  // ค่าถูกเปลี่ยนจากข้างนอก (โหลดข้อมูลมาใหม่ / กดล้างตัวกรอง) ต้องตามให้ทัน
  useEffect(() => {
    setText(value)
    setError(null)
  }, [value])

  /** พิมพ์เองได้ แต่ส่งขึ้นไปเฉพาะค่าที่ผ่านการตรวจ — กัน พ.ศ. ที่ paste เข้ามา */
  function commit(raw: string) {
    const s = raw.trim()
    if (s === '') {
      setError(allowEmpty ? null : 'ยังไม่ได้เลือกวันที่')
      if (allowEmpty) onChange('')
      return
    }
    const checked = checkDate(s)
    if (!checked.ok) {
      setError(checked.error)
      return
    }
    setError(null)
    onChange(checked.value)
  }

  return (
    <div className="dp" ref={box}>
      <div className="dp__field">
        <input
          id={id}
          type="text"
          className="dp__input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              commit(text)
            }
          }}
          placeholder="YYYY-MM-DD"
          inputMode="numeric"
          maxLength={10}
          autoComplete="off"
          spellCheck={false}
        />
        <button
          type="button"
          className="dp__open"
          onClick={() => setOpen((o) => !o)}
          aria-label="เปิดปฏิทิน"
          aria-expanded={open}
        >
          <Icon d={CAL_ICON} />
        </button>
      </div>

      {error && <small className="bad">{error}</small>}

      {open && (
        <Calendar
          value={checkDate(text).ok ? text : value}
          min={min}
          max={max}
          onPick={(iso) => {
            setText(iso)
            setError(null)
            onChange(iso)
            setOpen(false)
          }}
        />
      )}
    </div>
  )
}

/**
 * วันที่ + เวลา — ปฏิทินตัวเดียวกัน + ช่องเวลา `HH:mm`
 *
 * ค่าที่รับ/ส่งเป็น `YYYY-MM-DDTHH:mm` (รูปแบบที่ backend รับ ดู lib/offsite.js)
 * แยกช่องเวลาเป็น text ไม่ใช่ select เพราะช่วงเวลาเปิดใช้นาทีอะไรก็ได้ (เช่น 14:22)
 * select 60 ตัวเลือกหาช้ากว่าพิมพ์
 */
export function DateTimePicker({
  value,
  onChange,
  min,
  max,
  id,
}: {
  /** `YYYY-MM-DDTHH:mm` หรือ '' */
  value: string
  onChange: (value: string) => void
  min?: string
  max?: string
  id?: string
}) {
  const datePart = value.slice(0, 10)
  const timePart = value.length >= 16 ? value.slice(11, 16) : ''

  const [time, setTime] = useState(timePart)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setTime(value.length >= 16 ? value.slice(11, 16) : '')
  }, [value])

  function emit(date: string, t: string) {
    if (date === '') {
      setError('ยังไม่ได้เลือกวันที่')
      return
    }
    // ยังไม่กรอกเวลา = เที่ยงคืน (ตั้งใจ — ช่วงเวลาที่เริ่ม 00:00 คือค่าที่คนคาดหวังที่สุด)
    const checked = checkDateTime(`${date} ${t === '' ? '00:00' : t}`)
    if (!checked.ok) {
      setError(checked.error)
      return
    }
    setError(null)
    onChange(checked.value)
  }

  return (
    <div className="dp__dt">
      <DatePicker
        id={id}
        value={datePart}
        min={min?.slice(0, 10)}
        max={max?.slice(0, 10)}
        allowEmpty={false}
        onChange={(d) => emit(d, time)}
      />
      <input
        type="text"
        className="dp__time"
        value={time}
        onChange={(e) => setTime(e.target.value)}
        onBlur={() => emit(datePart, time)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            emit(datePart, time)
          }
        }}
        placeholder="HH:mm"
        inputMode="numeric"
        maxLength={5}
        autoComplete="off"
        aria-label="เวลา (HH:mm)"
      />
      {error && <small className="bad">{error}</small>}
    </div>
  )
}
